import { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck, Globe2, Building2, Lock, MessageSquare } from "lucide-react";
import { scrollToCalculator } from "@/lib/notify";
import ContactModal from "./ContactModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const TRUST = [
    { icon: ShieldCheck, label: "RBI PA-CB License Application In Progress" },
    { icon: Globe2, label: "SWIFT Enabled" },
    { icon: Building2, label: "Partnered with AD-I Banks" },
    { icon: Lock, label: "Secure Infrastructure" },
];

function MiniDashboard() {
    const [rate, setRate] = useState({ value: 83.35, change: 0.18, live: false });

    useEffect(() => {
        let cancelled = false;
        const fetchRate = async () => {
            try {
                const res = await fetch(`${BACKEND_URL}/api/fx/rates`);
                if (!res.ok) return;
                const data = await res.json();
                const usd = Array.isArray(data) ? data.find((r) => r.currency === "USD") : null;
                if (usd && !cancelled) {
                    setRate((prev) => {
                        const newVal = Number(usd.interbank_rate);
                        const change = prev.live && prev.value
                            ? ((newVal - prev.value) / prev.value) * 100
                            : 0.18;
                        return { value: newVal, change, live: true };
                    });
                }
            } catch (_) {
                /* keep static fallback */
            }
        };
        fetchRate();
        const id = setInterval(fetchRate, 60_000); // refresh every 60s
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    const formatted = rate.value.toFixed(2);
    const changeStr = `${rate.change >= 0 ? "+" : ""}${rate.change.toFixed(2)}%`;
    const changeColor = rate.change >= 0 ? "text-emerald-300" : "text-rose-300";

    return (
        <div className="relative w-full max-w-xl mx-auto lg:mx-0">
            {/* Floating glow rings */}
            <div className="pointer-events-none absolute -inset-10 -z-10 blur-3xl">
                <div className="absolute left-10 top-10 h-48 w-48 rounded-full bg-emerald-500/30" />
                <div className="absolute right-0 bottom-0 h-40 w-40 rounded-full bg-amber-400/20" />
            </div>

            <div className="rounded-3xl border border-white/10 bg-zinc-900/70 backdrop-blur-xl shadow-2xl p-5 sm:p-6 glow-emerald">
                {/* Top bar */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-300/70" />
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
                    </div>
                    <span className="text-[10px] tracking-widest uppercase text-zinc-400 font-mono">
                        truopay · dashboard
                    </span>
                </div>

                {/* Balances */}
                <div className="mt-5 grid grid-cols-3 gap-3">
                    {[
                        { ccy: "USD", amt: "128,540.20", flag: "🇺🇸" },
                        { ccy: "GBP", amt: "42,310.00", flag: "🇬🇧" },
                        { ccy: "INR", amt: "1,06,82,400", flag: "🇮🇳" },
                    ].map((b) => (
                        <div
                            key={b.ccy}
                            className="rounded-xl border border-white/10 bg-white/[0.03] p-3"
                        >
                            <div className="flex items-center justify-between text-xs text-zinc-400">
                                <span className="font-mono">{b.ccy}</span>
                                <span aria-hidden>{b.flag}</span>
                            </div>
                            <div className="mt-1 font-mono text-sm sm:text-base font-semibold text-white">
                                {b.amt}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Live rate */}
                <div className="mt-5 rounded-2xl border border-emerald-400/20 bg-gradient-to-r from-emerald-500/10 via-emerald-500/5 to-transparent p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] tracking-widest uppercase text-emerald-300/90 font-bold flex items-center gap-2">
                                <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                        rate.live ? "bg-emerald-400 animate-flow-pulse" : "bg-zinc-500"
                                    }`}
                                    aria-hidden
                                />
                                Live · USD / INR
                            </div>
                            <div
                                className="mt-1 font-mono text-2xl font-bold text-white"
                                data-testid="hero-live-rate"
                            >
                                {formatted}
                                <span className={`ml-2 text-xs ${changeColor}`}>{changeStr}</span>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-end gap-1 h-10">
                            {[6, 9, 7, 12, 10, 14, 11, 16, 13, 18, 15, 22].map((h, i) => (
                                <span
                                    key={i}
                                    className="w-1.5 rounded-sm bg-gradient-to-t from-emerald-600 to-emerald-300"
                                    style={{ height: `${h * 1.5}px` }}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Transactions */}
                <div className="mt-5 space-y-2">
                    {[
                        { who: "Acme Imports LLC", st: "INR Credited", amt: "+₹8,36,420", ok: true },
                        { who: "Stripe Payouts", st: "FIRA Generated", amt: "$4,200", ok: true },
                        { who: "Hyperlocal Ltd", st: "Compliance Check", amt: "£12,000", ok: false },
                    ].map((t, i) => (
                        <div
                            key={i}
                            className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5"
                        >
                            <div className="min-w-0">
                                <div className="truncate text-sm font-medium text-white">{t.who}</div>
                                <div className="text-[11px] text-zinc-400">{t.st}</div>
                            </div>
                            <div
                                className={`font-mono text-sm font-semibold ${
                                    t.ok ? "text-emerald-300" : "text-amber-300"
                                }`}
                            >
                                {t.amt}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Savings strip */}
                <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                    <div className="flex-1 rounded-2xl bg-gradient-to-r from-amber-300/15 via-amber-300/5 to-transparent border border-amber-300/30 px-4 py-3">
                        <div className="text-[10px] tracking-widest uppercase text-amber-300 font-bold">
                            You saved
                        </div>
                        <div className="font-mono text-lg font-bold text-white">₹24,300 vs banks</div>
                    </div>
                    <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-emerald-200 text-sm font-semibold flex items-center justify-center">
                        FIRA auto-generated
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Hero() {
    const [contactOpen, setContactOpen] = useState(false);
    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative isolate overflow-hidden bg-mesh-emerald text-white pt-28 md:pt-32 pb-20 md:pb-32"
        >
            <div className="absolute inset-0 bg-grid-emerald opacity-60" aria-hidden />
            {/* Glow blob */}
            <div
                aria-hidden
                className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[480px] w-[900px] rounded-full bg-emerald-500/20 blur-[120px]"
            />

            <div className="relative mx-auto max-w-7xl px-4 md:px-8">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
                    <div className="lg:col-span-6">
                        <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-bold text-emerald-300">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-flow-pulse" />
                            Cross-border infrastructure
                        </span>
                        <h1
                            data-testid="hero-headline"
                            className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.02] tracking-tight"
                        >
                            Cross-border payments
                            <br className="hidden sm:block" />{" "}
                            <span className="text-gradient-emerald">built for Indian businesses.</span>
                        </h1>
                        <p
                            data-testid="hero-subheadline"
                            className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed"
                        >
                            Send and receive global payments faster, cheaper, and with complete FX
                            transparency — without hidden bank spreads.
                        </p>

                        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
                            <button
                                type="button"
                                data-testid="hero-get-started-btn"
                                onClick={scrollToCalculator}
                                className="group inline-flex h-12 items-center justify-center gap-2 rounded-full bg-gold px-6 text-base font-bold text-zinc-950 glow-gold transition-all hover:-translate-y-0.5 hover:bg-gold-400"
                            >
                                Get Started
                                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <button
                                type="button"
                                data-testid="hero-contact-us-btn"
                                onClick={() => setContactOpen(true)}
                                className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 px-6 text-base font-semibold text-emerald-100 hover:bg-emerald-500/20 hover:border-emerald-400/60 transition-colors backdrop-blur-sm"
                            >
                                <MessageSquare className="h-4 w-4" />
                                Contact Us
                            </button>
                        </div>

                        <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                            {TRUST.map(({ icon: Icon, label }) => (
                                <li
                                    key={label}
                                    className="flex items-start gap-2.5 text-sm text-zinc-300"
                                >
                                    <Icon className="h-4 w-4 mt-0.5 text-emerald-300 shrink-0" />
                                    <span>{label}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-6">
                        <MiniDashboard />
                    </div>
                </div>
            </div>
            <ContactModal open={contactOpen} onOpenChange={setContactOpen} />
        </section>
    );
}
